import { Flex, Text } from '@chakra-ui/react';
import { FormFeedback, Input, TextArea } from '@morressier/alembic';
import {
  FieldValues,
  Path,
  RegisterOptions,
  get,
  useFormContext
} from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { ISubmissionStepContentForm } from './stepConfig';

const buildTextValidation = <
  TFieldValues extends FieldValues,
  TFieldName extends Path<TFieldValues>
>(
  fieldLabel: string,
  maxLength: number
): RegisterOptions<TFieldValues, TFieldName> => ({
  required: `${fieldLabel} is required`,
  maxLength: {
    value: maxLength,
    message: `${fieldLabel} cannot exceed ${maxLength} characters`
  },
  validate: value =>
    (typeof value === 'string' && value.trim().length > 0) ||
    `${fieldLabel} cannot contain only whitespace`
});

const StepContentFormField = ({
  label,
  name,
  placeholder,
  maxLength,
  isTextArea = false
}: {
  label: string;
  name: Path<ISubmissionStepContentForm>;
  placeholder: string;
  maxLength: number;
  isTextArea?: boolean;
}) => {
  const {
    register,
    watch,
    formState: { errors }
  } = useFormContext<ISubmissionStepContentForm>();

  const value = watch(name) as string;

  const fieldError = get(errors, name);
  const errorMessage =
    fieldError && typeof fieldError.message === 'string'
      ? fieldError.message
      : undefined;
  const currentLength = ((watch(name) as string | undefined) ?? '').length;

  return (
    <Flex flexDirection="column" flex="1" gap={2}>
      <FieldLabel label={label} subText="" isRequired />
      {isTextArea ? (
        <TextArea
          value={value}
          {...register(name, buildTextValidation(label, maxLength))}
          placeholder={placeholder}
          isInvalid={Boolean(errorMessage)}
          resize="none"
          maxLength={maxLength}
        />
      ) : (
        <Input
          {...register(name, buildTextValidation(label, maxLength))}
          placeholder={placeholder}
          isInvalid={Boolean(errorMessage)}
          maxLength={maxLength}
        />
      )}
      {errorMessage ? (
        <FormFeedback variant="error">{errorMessage}</FormFeedback>
      ) : null}
      <Text textStyle="textContext" alignSelf="flex-end">
        {currentLength}/{maxLength}
      </Text>
    </Flex>
  );
};

export default StepContentFormField;
